import type { FormEvent } from 'react'
import { Send, Settings2, Square } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface ChatComposerProps {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  onStop?: () => void
  onOpenSettings?: () => void
  streaming?: boolean
  showSettingsButton?: boolean
  disabled?: boolean
  sendLabel?: string
  className?: string
}

export function ChatComposer({
  value,
  onChange,
  onSend,
  onStop,
  onOpenSettings,
  streaming = false,
  showSettingsButton = false,
  disabled = false,
  sendLabel,
  className,
}: ChatComposerProps) {
  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (streaming) {
      onStop?.()
      return
    }
    if (!value.trim() || disabled) return
    onSend()
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={cn('border-t border-border bg-background p-3', className)}
      data-testid="chat-composer"
    >
      <div className="flex items-end gap-2">
        {showSettingsButton && onOpenSettings ? (
          <Button type="button" variant="outline" size="icon" onClick={onOpenSettings} aria-label="Settings">
            <Settings2 className="h-4 w-4" />
          </Button>
        ) : null}
        <textarea
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Type your prompt here..."
          rows={2}
          className="min-h-11 flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm"
          data-testid="chat-prompt-input"
        />
        {streaming ? (
          <Button
            type="submit"
            variant="outline"
            data-testid="chat-stop-button"
            aria-label="Stop generation"
          >
            <Square className="h-4 w-4" />
            Stop
          </Button>
        ) : (
          <Button type="submit" disabled={disabled || !value.trim()} data-testid="chat-send-button">
            <Send className="h-4 w-4" />
            {sendLabel ?? 'Send'}
          </Button>
        )}
      </div>
    </form>
  )
}
